import { formatTimestamp, normalizeTimezone } from "../../lib/time";
import type { WorkflowNode, WorkflowRunHistoryEntry, WorkflowRunItem } from "./api";

export type OutcomeTone = "green" | "red" | "neutral";

export interface JourneyOutcome {
  field: string;
  label: string;
  value: string;
  tone: OutcomeTone;
  expected?: string;
  actual?: string;
}

export interface JourneyCard {
  key: string;
  nodeId: string;
  nodeLabel: string;
  agentLabel: string;
  at?: string;
  callId?: string;
  decision?: string;
  reason?: string;
  outcomes: JourneyOutcome[];
  movedToLabel?: string | null;
  blocked: boolean;
  blockedCopy?: string;
  scheduledFor?: string;
  pending?: boolean;
}

function stringify(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value).trim();
}

function isEmptyValue(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return !normalized || normalized === "na" || normalized === "n/a" || normalized === "null" || normalized === "none";
}

export function nodeLabel(nodes: WorkflowNode[] | undefined, nodeId?: string | null): string {
  if (!nodeId) return "";
  const node = nodes?.find((item) => item.node_id === nodeId);
  return node?.label || nodeId;
}

export function agentLabel(
  nodes: WorkflowNode[] | undefined,
  nodeId?: string | null,
  fallback?: string,
): string {
  const node = nodes?.find((item) => item.node_id === nodeId);
  return node?.agent_name || fallback || node?.agent_id || "";
}

function conditionsForField(entry: WorkflowRunHistoryEntry, field: string) {
  const evaluations = entry.edge_evaluations || [];
  const matches: Array<{ matched?: boolean; expected?: unknown; actual?: unknown }> = [];
  for (const evaluation of evaluations) {
    for (const condition of evaluation.conditions || []) {
      if (condition.field === field) matches.push(condition);
    }
  }
  return matches;
}

export function buildJourneyCards(
  run: WorkflowRunItem,
  nodes: WorkflowNode[] | undefined,
): JourneyCard[] {
  const history = run.history || [];
  if (history.length === 0) {
    return [
      {
        key: "pending",
        nodeId: run.current_node_id,
        nodeLabel: nodeLabel(nodes, run.current_node_id),
        agentLabel: agentLabel(nodes, run.current_node_id),
        pending: true,
        outcomes: [],
        blocked: false,
      },
    ];
  }

  return history.map((entry, index) => {
    const decision = entry.decision || "";
    const blocked = decision === "ended_no_match" || decision === "exhausted" || decision === "failed";
    const advanced = decision === "advanced" || (decision === "completed" && Boolean(entry.matched_edge_id));

    const outcomes: JourneyOutcome[] = [];
    for (const field of entry.enabled_outfields || []) {
      const value = stringify(field.value);
      const set = field.is_set === true || (Boolean(value) && !isEmptyValue(value));
      const conditions = conditionsForField(entry, field.field);
      const anyMatched = conditions.some((condition) => condition.matched === true);
      const usedInEval = conditions.length > 0;

      let tone: OutcomeTone = "neutral";
      if ((advanced || decision === "completed") && anyMatched) {
        tone = "green";
      } else if (blocked && (set || usedInEval)) {
        tone = "red";
      }

      const show = tone !== "neutral" || (set && !isEmptyValue(value));
      if (!show) continue;

      const failed = conditions.find((condition) => condition.matched === false);
      const matched = conditions.find((condition) => condition.matched === true);
      outcomes.push({
        field: field.field,
        label: field.label || field.field,
        value: value || "—",
        tone,
        expected: failed
          ? stringify(failed.expected)
          : matched
            ? stringify(matched.expected)
            : undefined,
        actual: failed ? stringify(failed.actual) : undefined,
      });
    }

    if (blocked && outcomes.length === 0) {
      outcomes.push({
        field: "outcome",
        label: "Outcome",
        value: entry.reason || "No matching transition",
        tone: "red",
      });
    }

    return {
      key: `${entry.call_id || entry.node_id}-${index}`,
      nodeId: entry.node_id,
      nodeLabel: nodeLabel(nodes, entry.node_id),
      agentLabel: agentLabel(nodes, entry.node_id, entry.agent_id),
      at: entry.at,
      callId: entry.call_id,
      decision,
      reason: entry.reason,
      outcomes,
      movedToLabel: !blocked && entry.next_node_id ? nodeLabel(nodes, entry.next_node_id) : null,
      blocked,
      blockedCopy:
        decision === "ended_no_match"
          ? "This is the outcome that was filled, so the contact did not move to the next agent."
          : blocked
            ? entry.reason || "This contact did not move to the next agent."
            : undefined,
      scheduledFor: entry.scheduled_for_utc,
    };
  });
}

export function formatWhen(iso?: string | null, timeZone?: string | null): string {
  if (!iso) return "";
  const tz = normalizeTimezone(timeZone);
  return formatTimestamp(iso, tz);
}

export function formatNextAction(raw?: string | null, timeZone?: string | null): string {
  if (!raw) return "—";
  if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) {
    const formatted = formatWhen(raw, timeZone);
    return formatted ? `Next call ${formatted}` : raw;
  }
  return raw;
}
